import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Input,
  Select,
  Switch,
  useToast,
} from "@/core/ui";
import { DifficultyStars } from "@/core/puzzles/PuzzleListPage";
import {
  EMPTY_FILTERS,
  PUZZLE_STATUSES,
  statusCounts,
  useBacklog,
  useCreatePuzzle,
  usePuzzleTypes,
  type BacklogFilters,
  type PuzzleStatus,
} from "./api";
import { StatusBadge } from "./status";

export function BacklogPage() {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<BacklogFilters>(EMPTY_FILTERS);
  const [creating, setCreating] = useState(false);
  const list = useBacklog(filters);
  const unfiltered = useBacklog(EMPTY_FILTERS);
  const types = usePuzzleTypes();
  const counts = useMemo(() => statusCounts(unfiltered.data), [unfiltered.data]);

  const toggleStatus = (status: PuzzleStatus) =>
    setFilters((f) => ({
      ...f,
      status: f.status.includes(status)
        ? f.status.filter((s) => s !== status)
        : [...f.status, status],
    }));

  return (
    <section>
      <div className="bt-page-header">
        <h1 className="bt-page-title">{t("backlog.title")}</h1>
        <Button onClick={() => setCreating(true)}>{t("backlog.new")}</Button>
      </div>

      <Card className="bt-backlog__filters">
        <div className="bt-backlog__filter-row">
          <div className="bt-backlog__filter-search">
            <Field label={t("backlog.search")} htmlFor="backlog-search">
              <Input
                id="backlog-search"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              />
            </Field>
          </div>
          <div className="bt-backlog__filter-type">
            <Field label={t("backlog.type")} htmlFor="backlog-type">
              <Select
                id="backlog-type"
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              >
                <option value="">{t("backlog.all_types")}</option>
                {(types.data ?? []).map((type) => (
                  <option key={type.key} value={type.key}>
                    {t(`puzzle_types.${type.key}`, { defaultValue: type.key })}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="bt-backlog__filter-switch">
            <Switch
              id="backlog-mine"
              checked={filters.mine}
              onChange={(mine) => setFilters({ ...filters, mine })}
              label={t("backlog.mine")}
            />
          </div>
        </div>
        <div
          className="bt-backlog__status-filters"
          role="group"
          aria-label={t("backlog.filter_status")}
        >
          {PUZZLE_STATUSES.map((status) => {
            const active = filters.status.includes(status);
            return (
              <button
                key={status}
                type="button"
                aria-pressed={active}
                onClick={() => toggleStatus(status)}
                className="bt-status-toggle"
              >
                {t(`backlog.status.${status}`)}
                {counts ? (
                  <span className="bt-status-toggle__count">{counts[status] ?? 0}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </Card>

      {list.isPending ? <p className="bt-page-message">{t("app.loading")}</p> : null}
      {list.isError ? <Alert tone="danger">{t("app.error_generic")}</Alert> : null}
      {list.data && list.data.results.length === 0 ? (
        <p className="bt-page-message">{t("backlog.empty")}</p>
      ) : null}

      <ul className="bt-backlog__list">
        {(list.data?.results ?? []).map((puzzle) => (
          <li key={puzzle.slug}>
            <Link to={`/backlog/${puzzle.slug}`} className="bt-backlog__item-link">
              <div className="bt-backlog__item-header">
                <h2 className="bt-backlog__item-title">{puzzle.title}</h2>
                <StatusBadge status={puzzle.status} />
                <Badge tone="info">
                  {t(`puzzle_types.${puzzle.puzzle_type}`, { defaultValue: puzzle.puzzle_type })}
                </Badge>
                <span className="bt-backlog__version">
                  {t("backlog.version", { number: puzzle.version_number })}
                </span>
                <span className="bt-backlog__item-difficulty">
                  <DifficultyStars value={puzzle.difficulty} />
                </span>
              </div>
              <p className="bt-backlog__item-author">
                {t("backlog.author", { author: puzzle.author_name })}
                {puzzle.summary ? ` · ${puzzle.summary.description}` : ""}
              </p>
              <div className="bt-backlog__item-meta">
                {puzzle.open_comments > 0 ? (
                  <Badge tone="warning">
                    {t("backlog.open_comments", { count: puzzle.open_comments })}
                  </Badge>
                ) : null}
                {puzzle.starts_at ? (
                  <span>
                    {t("backlog.scheduled_for", {
                      date: new Date(puzzle.starts_at).toLocaleString(i18n.language),
                    })}
                  </span>
                ) : null}
                {puzzle.tags.map((tag) => (
                  <Badge key={tag}>{tag}</Badge>
                ))}
              </div>
            </Link>
          </li>
        ))}
      </ul>

      <CreatePuzzleDialog open={creating} onClose={() => setCreating(false)} />
    </section>
  );
}

function CreatePuzzleDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const types = usePuzzleTypes();
  const create = useCreatePuzzle();
  const [title, setTitle] = useState("");
  const [type, setType] = useState("");
  const [difficulty, setDifficulty] = useState(3);
  const available = types.data ?? [];
  const chosen = type || available[0]?.key || "";

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!title.trim() || !chosen) return;
    create.mutate(
      { title: title.trim(), puzzle_type: chosen, difficulty },
      {
        onSuccess: () => {
          setTitle("");
          onClose();
        },
        onError: () => toast(t("app.error_generic"), "danger"),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title={t("backlog.new_title")}>
      <form onSubmit={submit}>
        <Field label={t("backlog.title_field")} htmlFor="new-title">
          <Input id="new-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        <Field label={t("backlog.type")} htmlFor="new-type">
          <Select id="new-type" value={chosen} onChange={(e) => setType(e.target.value)}>
            {available.map((info) => (
              <option key={info.key} value={info.key}>
                {t(`puzzle_types.${info.key}`, { defaultValue: info.key })}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("backlog.difficulty")} htmlFor="new-difficulty">
          <Select
            id="new-difficulty"
            value={String(difficulty)}
            onChange={(e) => setDifficulty(Number(e.target.value))}
          >
            {[1, 2, 3, 4, 5].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        </Field>
        <div className="bt-form__actions bt-form__actions--end">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("backlog.cancel")}
          </Button>
          <Button type="submit" loading={create.isPending}>
            {t("backlog.create")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
